import { useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { HiMinus, HiPlus, HiPrinter } from 'react-icons/hi';
import {
    MdFileDownload,
    MdOutlineFirstPage,
    MdOutlineLastPage,
    MdOutlineNavigateBefore,
    MdOutlineNavigateNext,
} from 'react-icons/md';
import { useReactToPrint } from 'react-to-print';

import { showErrorToast } from '../Toasts/toast';

interface ControlPanelProps {
    file: File;
    pageNumber: number;
    numPages: number;
    setPageNumber: React.Dispatch<React.SetStateAction<number>>;
    scale: number;
    setScale: React.Dispatch<React.SetStateAction<number>>;
    printRef: React.MutableRefObject<React.ReactInstance>;
    fileURL: string;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
    file,
    pageNumber,
    numPages,
    setPageNumber,
    scale,
    setScale,
    printRef,
    fileURL,
}) => {
    const [shouldPrint, setShouldPrint] = useState<boolean>(false);
    const [timeoutAlreadyRunning, setTimeoutAlreadyRunning] = useState<boolean>(false);

    const isFirstPage = pageNumber === 1;
    const isLastPage = pageNumber === numPages;
    const hasPreviousPage = !isFirstPage;
    const hasNextPage = !isLastPage;

    const firstPageClass = isFirstPage ? 'disabled' : 'clickable';
    const lastPageClass = isLastPage ? 'disabled' : 'clickable';
    const previousPageClass = !hasPreviousPage ? 'disabled' : 'clickable';
    const nextPageClass = !hasNextPage ? 'disabled' : 'clickable';

    const fileName = useMemo(() => {
        return `${file?.name ?? 'commission-statement'}.pdf`;
    }, [file?.name]);

    const goToFirstPage = () => {
        if (!isFirstPage) setPageNumber(1);
    };
    const goToPreviousPage = () => {
        if (!isFirstPage) setPageNumber(pageNumber - 1);
    };
    const goToNextPage = () => {
        if (!isLastPage) setPageNumber(pageNumber + 1);
    };
    const goToLastPage = () => {
        if (!isLastPage) setPageNumber(numPages);
    };
    let scaleBeforePrint = 1;

    const onPageChange = (e) => {
        const { value } = e.target;
        const newPageNumber =
            Number(value) < 1 ? 1 : Number(value) > numPages ? numPages : Number(value);
        setPageNumber(newPageNumber);
    };

    const isMinZoom = scale < 0.6;
    const isMaxZoom = scale >= 2.0;

    const zoomOutClass = isMinZoom ? 'disabled' : 'clickable';
    const zoomInClass = isMaxZoom ? 'disabled' : 'clickable';

    const zoomOut = () => {
        if (!isMinZoom) setScale(scale - 0.1);
    };

    const zoomIn = () => {
        if (!isMaxZoom) setScale(scale + 0.1);
    };

    const handleSetScaleBeforePrint = () => {
        scaleBeforePrint = scale;
        setScale(1.4);
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: fileName,
        onPrintError: () => showErrorToast('there is an error when printing'),
        onAfterPrint: () => {
            setScale(scaleBeforePrint);
        },
        pageStyle: `@media print {
      @page {
        margin-bottom: 0 !important;
        margin-top: 0 !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
    }`,
    });

    useEffect(() => {
        if (shouldPrint && !timeoutAlreadyRunning) {
            // Wait for it to render to correct size first then do the printing
            setTimeoutAlreadyRunning(true);
            const id = setTimeout(() => {
                handlePrint();
                setTimeoutAlreadyRunning(false);
                setShouldPrint(false);
            }, 1000);

            return () => {
                clearTimeout(id);
            };
        }
        // Intentionally keeping this the dependency array shouldPrint, setShouldPrint
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldPrint, setShouldPrint]);

    return (
        <Box
            sx={{
                background: '#333639',
                height: '46px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '24px',
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'none',
                    padding: '16px',
                }}
            ></Box>

            <Box
                display={'flex'}
                flexDirection={'row'}
                gap={'16px'}
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Box
                    sx={{
                        color: '#fff',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '9px',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        style={{
                            borderRadius: '100%',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                            paddingLeft: '5px',
                            paddingRight: '5px',
                        }}
                        sx={{
                            '&:hover:not(.disabled)': {
                                background: 'rgba(150,150,150,0.5)',
                                cursor: 'pointer',
                            },
                            '&.disabled svg': {
                                fill: 'rgba(150,150,150,0.5)',
                            },
                        }}
                        className={firstPageClass}
                        title="Go to First Page"
                    >
                        <MdOutlineFirstPage onClick={!isFirstPage ? goToFirstPage : null} />
                    </Box>
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        style={{
                            borderRadius: '100%',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                            paddingLeft: '5px',
                            paddingRight: '5px',
                        }}
                        sx={{
                            '&:hover:not(.disabled)': {
                                background: 'rgba(150,150,150,0.5)',
                                cursor: 'pointer',
                            },
                            '&.disabled svg': {
                                fill: 'rgba(150,150,150,0.5)',
                            },
                        }}
                        className={previousPageClass}
                        title="Go to Previous Page"
                    >
                        <MdOutlineNavigateBefore onClick={goToPreviousPage} />
                    </Box>
                    <input
                        style={{
                            color: '#fff',
                            backgroundColor: '#191B1C',
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            paddingLeft: '4px',
                            paddingRight: '4px',
                            border: 'none',
                            boxShadow: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                        name="pageNumber"
                        type="number"
                        min={1}
                        max={numPages || 1}
                        className="p-0 pl-1 mx-2"
                        value={pageNumber}
                        onChange={onPageChange}
                    />
                    <Box>/</Box> <Box>{numPages}</Box>
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        style={{
                            borderRadius: '100%',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                            paddingLeft: '5px',
                            paddingRight: '5px',
                        }}
                        sx={{
                            '&:hover:not(.disabled)': {
                                background: 'rgba(150,150,150,0.5)',
                                cursor: 'pointer',
                            },
                            '&.disabled svg': {
                                fill: 'rgba(150,150,150,0.5)',
                            },
                        }}
                        className={nextPageClass}
                        title="Go to Next Page"
                    >
                        <MdOutlineNavigateNext onClick={goToNextPage} />
                    </Box>
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        style={{
                            borderRadius: '100%',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                            paddingLeft: '5px',
                            paddingRight: '5px',
                        }}
                        sx={{
                            '&:hover:not(.disabled)': {
                                background: 'rgba(150,150,150,0.5)',
                                cursor: 'pointer',
                            },
                            '&.disabled svg': {
                                fill: 'rgba(150,150,150,0.5)',
                            },
                        }}
                        className={lastPageClass}
                        title="Go to Last Page"
                    >
                        <MdOutlineLastPage onClick={goToLastPage} />
                    </Box>
                    <Box sx={{ color: '#bbb' }}>|</Box>
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        gap={'4px'}
                        padding={'8px'}
                    >
                        <Box
                            style={{
                                borderRadius: '100%',
                                paddingTop: '5px',
                                paddingBottom: '5px',
                                paddingLeft: '8px',
                                paddingRight: '8px',
                            }}
                            sx={{
                                '&:hover:not(.disabled)': {
                                    background: 'rgba(150,150,150,0.5)',
                                    cursor: 'pointer',
                                },
                                '&.disabled svg': {
                                    fill: 'rgba(150,150,150,0.5)',
                                },
                            }}
                            onClick={zoomOut}
                            className={zoomOutClass}
                            title="Zoom Out"
                        >
                            <HiMinus color="#fff" className={`${zoomOutClass}`} />
                        </Box>
                        <span
                            style={{
                                color: '#fff',
                                paddingTop: '2px',
                                paddingBottom: '2px',
                                paddingLeft: '8px',
                                paddingRight: '8px',
                            }}
                        >
                            {(scale * 100).toFixed()}%
                        </span>
                        <Box
                            style={{
                                borderRadius: '100%',
                                paddingTop: '5px',
                                paddingBottom: '5px',
                                paddingLeft: '8px',
                                paddingRight: '8px',
                            }}
                            sx={{
                                '&:hover:not(.disabled)': {
                                    background: 'rgba(150,150,150,0.5)',
                                    cursor: 'pointer',
                                },
                                '&.disabled svg': {
                                    fill: 'rgba(150,150,150,0.5)',
                                },
                            }}
                            className={zoomInClass}
                            onClick={zoomIn}
                            title="Zoom In"
                        >
                            <HiPlus color="#fff" className={`${zoomInClass}`} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'center'}
                alignItems={'center'}
                sx={{
                    height: '100%',
                    width: '32px',
                }}
            >
                <Box
                    style={{
                        borderRadius: '100%',
                        paddingTop: '5px',
                        paddingBottom: '5px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                    }}
                    sx={{
                        '&:hover': {
                            background: 'rgba(150,150,150,0.5)',
                            cursor: 'pointer',
                        },
                    }}
                    onClick={() => {
                        handleSetScaleBeforePrint();
                        setShouldPrint(true);
                    }}
                    title="Print Statement"
                >
                    <HiPrinter color="#fff" size={20} />
                </Box>
                <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    style={{
                        borderRadius: '100%',
                        paddingTop: '5px',
                        paddingBottom: '5px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                    }}
                    sx={{
                        '&:hover': {
                            background: 'rgba(150,150,150,0.5)',
                            cursor: 'pointer',
                        },
                    }}
                >
                    <a
                        href={fileURL ? fileURL : null}
                        download={`${file?.name ?? 'commission-statement'}.pdf`}
                        title="Download PDF"
                    >
                        <MdFileDownload color="#fff" size={20} />
                    </a>
                </Box>
            </Box>
        </Box>
    );
};

export default ControlPanel;
